import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import ExportModal from '../components/ExportModal';
import ImportModal from '../components/ImportModal';
import RecurringPayments from '../components/RecurringPayments';
import toast from 'react-hot-toast';
import { 
  isPushSupported, 
  getNotificationPermission, 
  subscribeToPush, 
  unsubscribeFromPush,
  isSubscribedToPush 
} from '../utils/pushNotifications';
import { nativeFetch } from '../utils/nativeApi';

function SettingsPage() {
  const { t, i18n } = useTranslation(['settings', 'common']);
  const [settings, setSettings] = useState({
    name: '',
    email: '',
    main_currency: 'ILS',
    ui_language: 'he',
    monthly_budget: null,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  
  // Household state
  const [household, setHousehold] = useState(null);
  const [householdLoading, setHouseholdLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [householdName, setHouseholdName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [householdSaving, setHouseholdSaving] = useState(false);
  
  // Export/Import state
  const [showExportModal, setShowExportModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Push notification state
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchHousehold();
    checkPushStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // PWA Install Prompt Handler
  useEffect(() => {
    console.log('[PWA] Checking installation status...');
    
    // Check if already installed (multiple detection methods)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
    const isIOSStandalone = window.navigator.standalone === true;
    
    console.log('[PWA] isStandalone:', isStandalone);
    console.log('[PWA] isIOSStandalone:', isIOSStandalone);
    
    // Check if running in standalone mode
    if (isStandalone || isIOSStandalone) {
      console.log('[PWA] Running in standalone mode - marking as installed');
      setIsInstalled(true);
      return;
    }

    // Check localStorage immediately (but don't trust it if not in standalone mode)
    const wasInstalled = localStorage.getItem('pwa_installed');
    console.log('[PWA] localStorage pwa_installed:', wasInstalled);
    
    // Only trust localStorage if we're actually in standalone mode
    // This prevents false positives after uninstalling
    if (wasInstalled === 'true' && !isStandalone && !isIOSStandalone) {
      console.log('[PWA] Clearing stale pwa_installed flag');
      localStorage.removeItem('pwa_installed');
    }

    // For Android, check if beforeinstallprompt was already consumed
    // If the app was installed and then opened in browser, the event won't fire
    let promptFired = false;
    
    // Listen for the beforeinstallprompt event
    const handleBeforeInstallPrompt = (e) => {
      console.log('[PWA] beforeinstallprompt event fired!');
      promptFired = true;
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      setIsInstallable(true);
    };

    // Listen for successful installation
    const handleAppInstalled = () => {
      console.log('[PWA] appinstalled event fired!');
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
      localStorage.setItem('pwa_installed', 'true');
      toast.success('App installed successfully! 🎉');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    // After a short delay, check if prompt fired
    const checkTimeout = setTimeout(() => {
      console.log('[PWA] Timeout check - promptFired:', promptFired);
      if (!promptFired) {
        console.log('[PWA] No prompt fired - this is normal if already installed or not installable');
      }
    }, 3000);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      clearTimeout(checkTimeout);
    };
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await nativeFetch('/api/settings');

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        // Sync i18n language with user's ui_language setting
        if (data.ui_language && data.ui_language !== i18n.language) {
          i18n.changeLanguage(data.ui_language);
        }
      } else {
        toast.error('Failed to fetch settings');
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchHousehold = async () => {
    try {
      const response = await nativeFetch('/api/household');
      if (response.ok) {
        const data = await response.json();
        setHousehold(data);
      }
    } catch (error) {
      console.error('Error fetching household:', error);
    } finally {
      setHouseholdLoading(false);
    }
  };

  const checkPushStatus = async () => {
    const supported = isPushSupported();
    setPushSupported(supported);
    
    if (supported) {
      setPushPermission(getNotificationPermission());
      const subscribed = await isSubscribedToPush();
      setPushSubscribed(subscribed);
    }
  };

  const handleEnablePush = async () => {
    setPushLoading(true);
    try {
      await subscribeToPush();
      setPushSubscribed(true);
      setPushPermission('granted');
      toast.success(t('notifications.enabled'));
    } catch (error) {
      console.error('Error enabling push:', error);
      if (error.message === 'Notification permission denied') {
        setPushPermission('denied');
        toast.error(t('notifications.permissionDenied'));
      } else {
        toast.error(t('notifications.enableError'));
      }
    } finally {
      setPushLoading(false);
    }
  };

  const handleDisablePush = async () => {
    setPushLoading(true);
    try {
      await unsubscribeFromPush();
      setPushSubscribed(false);
      toast.success(t('notifications.disabled'));
    } catch (error) {
      console.error('Error disabling push:', error);
      toast.error(t('notifications.disableError'));
    } finally {
      setPushLoading(false);
    }
  };

  const handleCreateHousehold = async () => {
    if (!householdName.trim()) {
      toast.error('Please enter a household name');
      return;
    }
    setHouseholdSaving(true);
    try {
      const response = await nativeFetch('/api/household', {
        method: 'POST',
        body: JSON.stringify({ name: householdName.trim() }),
      });
      if (response.ok) {
        const data = await response.json();
        setHousehold(data);
        setShowCreateModal(false);
        setHouseholdName('');
        toast.success('Household created!');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to create household');
      }
    } catch (error) {
      toast.error('Failed to create household');
    } finally {
      setHouseholdSaving(false);
    }
  };

  const handleJoinHousehold = async () => {
    if (!inviteCode.trim() || inviteCode.length !== 6) {
      toast.error('Please enter a valid 6-digit code');
      return;
    }
    setHouseholdSaving(true);
    try {
      const response = await nativeFetch('/api/household/join', {
        method: 'POST',
        body: JSON.stringify({ invite_code: inviteCode.trim() }),
      });
      if (response.ok) {
        const data = await response.json();
        setHousehold(data);
        setShowJoinModal(false);
        setInviteCode('');
        toast.success('Joined household!');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to join household');
      }
    } catch (error) {
      toast.error('Failed to join household');
    } finally {
      setHouseholdSaving(false);
    }
  };

  const handleLeaveHousehold = async () => {
    if (!window.confirm('Are you sure you want to leave this household?')) return;
    try {
      const response = await nativeFetch('/api/household/leave', {
        method: 'POST',
      });
      if (response.ok) {
        setHousehold(null);
        toast.success('Left household');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to leave household');
      }
    } catch (error) {
      toast.error('Failed to leave household');
    }
  };

  const handleDeleteHousehold = async () => {
    if (!window.confirm('Are you sure you want to delete this household? All members will be removed.')) return;
    try {
      const response = await nativeFetch('/api/household', {
        method: 'DELETE',
      });
      if (response.ok) {
        setHousehold(null);
        toast.success('Household deleted');
      } else {
        const error = await response.json();
        toast.error(error.error || 'Failed to delete household');
      }
    } catch (error) {
      toast.error('Failed to delete household');
    }
  };

  const copyInviteCode = () => {
    if (household?.invite_code) {
      navigator.clipboard.writeText(household.invite_code);
      toast.success('Invite code copied!');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const response = await nativeFetch('/api/settings', {
        method: 'PUT',
        body: JSON.stringify({
          name: settings.name,
          main_currency: settings.main_currency,
          ui_language: settings.ui_language,
          monthly_budget: settings.monthly_budget ? parseFloat(settings.monthly_budget) : null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSettings(data);
        // Sync i18n language
        if (data.ui_language && data.ui_language !== i18n.language) {
          i18n.changeLanguage(data.ui_language);
        }
        toast.success(t('toast.saved'));
      } else {
        toast.error(t('toast.error'));
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      toast.error(t('toast.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInstallClick = async () => {
    if (!deferredPrompt) {
      toast.error('Installation not available');
      return;
    }

    // Show the install prompt
    deferredPrompt.prompt();

    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === 'accepted') {
      toast.success('Installing app...');
      // Mark as installed in localStorage
      localStorage.setItem('pwa_installed', 'true');
      setIsInstalled(true);
    } else {
      toast.error('Installation cancelled');
    }

    // Clear the deferredPrompt
    setDeferredPrompt(null);
    setIsInstallable(false);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="text-center text-2xl font-bold">{t('common:messages.loading')}</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto p-4 pb-24">
        <h1 className="text-4xl font-black mb-8">{t('title')}</h1>

        <form onSubmit={handleSubmit} className="card space-y-6">
          {/* Name */}
          <div>
            <label className="block text-lg font-bold mb-2">{t('profile.name')}</label>
            <input
              type="text"
              value={settings.name}
              onChange={(e) => handleChange('name', e.target.value)}
              className="input"
              required
            />
          </div>

          {/* Email (read-only) */}
          <div>
            <label className="block text-lg font-bold mb-2">{t('profile.email')}</label>
            <input
              type="email"
              value={settings.email}
              className="input bg-gray-100 cursor-not-allowed"
              disabled
            />
            <p className="text-sm text-gray-600 mt-1">
              {t('profile.emailHint', 'Email cannot be changed (linked to Google account)')}
            </p>
          </div>

          {/* Main Currency */}
          <div>
            <label className="block text-lg font-bold mb-2">{t('preferences.currency')}</label>
            <select
              value={settings.main_currency}
              onChange={(e) => handleChange('main_currency', e.target.value)}
              className="input"
            >
              <option value="ILS">ILS (Israeli Shekel)</option>
              <option value="USD">USD (US Dollar)</option>
              <option value="EUR">EUR (Euro)</option>
              <option value="GEL">GEL (Georgian Lari)</option>
              <option value="JPY">JPY (Japanese Yen)</option>
            </select>
            <p className="text-sm text-gray-600 mt-1">
              {t('preferences.currencyHint', 'Used when currency is not specified in expense')}
            </p>
          </div>

          {/* UI Language */}
          <div>
            <label className="block text-lg font-bold mb-2">{t('preferences.uiLanguage')}</label>
            <select
              value={settings.ui_language}
              onChange={(e) => handleChange('ui_language', e.target.value)}
              className="input"
            >
              <option value="he">🇮🇱 עברית (Hebrew)</option>
              <option value="en">🇺🇸 English</option>
            </select>
            <p className="text-sm text-gray-600 mt-1">
              {i18n.language === 'he' ? 'שפת הממשק של האפליקציה' : 'App interface language'}
            </p>
          </div>

          {/* Monthly Budget */}
          <div>
            <label className="block text-lg font-bold mb-2">{t('preferences.monthlyBudget')}</label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={settings.monthly_budget || ''}
              onChange={(e) => handleChange('monthly_budget', e.target.value)}
              className="input"
              placeholder={t('preferences.budgetPlaceholder')}
            />
            <p className="text-sm text-gray-600 mt-1">
              {t('preferences.budgetHint', 'Set a monthly spending limit to track your budget progress')}
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="btn-primary"
              disabled={saving}
            >
              {saving ? t('common:buttons.saving', 'Saving...') : t('common:buttons.save')}
            </button>
          </div>
        </form>

        {/* Household Section */}
        <div className="card mt-8">
          <h2 className="text-2xl font-black mb-4">👨‍👩‍👧 {t('household.title')}</h2>
          
          {householdLoading ? (
            <p className="text-gray-600">{t('common:messages.loading')}</p>
          ) : household ? (
            <div className="space-y-4">
              {/* Household Info */}
              <div className="p-4 bg-primary/20 border-2 border-black rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-xl font-bold">{household.name}</h3>
                  {household.created_by === settings.id && (
                    <span className="text-xs bg-black text-white px-2 py-1 rounded font-bold">{t('household.creator')}</span>
                  )}
                </div>
                
                {/* Invite Code */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm text-gray-600">{t('household.inviteCode')}:</span>
                  <button
                    onClick={copyInviteCode}
                    className="px-3 py-1 bg-white border-2 border-black rounded-lg font-mono font-bold text-lg hover:bg-gray-100 transition-colors"
                  >
                    {household.invite_code} 📋
                  </button>
                </div>
                
                {/* Members */}
                <div className="mb-3">
                  <span className="text-sm font-bold">{t('household.members')} ({household.members?.length || 0}):</span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {household.members?.map((member) => (
                      <span
                        key={member.id}
                        className="px-2 py-1 bg-white border-2 border-black rounded-lg text-sm font-bold"
                      >
                        {member.name}
                        {member.id === settings.id && ` (${t('household.you', 'You')})`}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex flex-wrap gap-2">
                {household.created_by === settings.id ? (
                  <button
                    onClick={handleDeleteHousehold}
                    className="px-4 py-2 border-2 border-red-500 text-red-500 rounded-lg font-bold hover:bg-red-50 transition-colors"
                  >
                    🗑️ {t('household.delete')}
                  </button>
                ) : (
                  <button
                    onClick={handleLeaveHousehold}
                    className="px-4 py-2 border-2 border-red-500 text-red-500 rounded-lg font-bold hover:bg-red-50 transition-colors"
                  >
                    🚪 {t('household.leave')}
                  </button>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-gray-600">
                {t('household.createOrJoin')}
              </p>
              <div className="flex flex-wrap gap-3">
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 border-2 border-black rounded-lg font-bold bg-primary shadow-hard-sm hover:opacity-90 transition-opacity"
                >
                  ➕ {t('household.create')}
                </button>
                <button
                  onClick={() => setShowJoinModal(true)}
                  className="px-4 py-2 border-2 border-black rounded-lg font-bold bg-white shadow-hard-sm hover:bg-gray-100 transition-colors"
                >
                  🔗 {t('household.join')}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Create Household Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="card max-w-md w-full">
              <h3 className="text-xl font-black mb-4">{t('household.create')}</h3>
              <input
                type="text"
                value={householdName}
                onChange={(e) => setHouseholdName(e.target.value)}
                placeholder={t('household.namePlaceholder')}
                className="input mb-4"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowCreateModal(false); setHouseholdName(''); }}
                  className="flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold bg-white hover:bg-gray-100"
                >
                  {t('common:buttons.cancel')}
                </button>
                <button
                  onClick={handleCreateHousehold}
                  disabled={householdSaving}
                  className="flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold bg-primary shadow-hard-sm hover:opacity-90 disabled:opacity-50"
                >
                  {householdSaving ? t('common:buttons.creating', 'Creating...') : t('common:buttons.create', 'Create')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Join Household Modal */}
        {showJoinModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="card max-w-md w-full">
              <h3 className="text-xl font-black mb-4">{t('household.join')}</h3>
              <p className="text-sm text-gray-600 mb-3">{t('household.enterCode')}</p>
              <input
                type="text"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="input text-center text-2xl font-mono tracking-widest mb-4"
                maxLength={6}
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={() => { setShowJoinModal(false); setInviteCode(''); }}
                  className="flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold bg-white hover:bg-gray-100"
                >
                  {t('common:buttons.cancel')}
                </button>
                <button
                  onClick={handleJoinHousehold}
                  disabled={householdSaving || inviteCode.length !== 6}
                  className="flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold bg-primary shadow-hard-sm hover:opacity-90 disabled:opacity-50"
                >
                  {householdSaving ? t('common:buttons.joining', 'Joining...') : t('household.join')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Recurring Payments Section */}
        <div className="card mt-8">
          <h2 className="text-2xl font-black mb-4">🔄 {t('recurring.title')}</h2>
          <RecurringPayments />
        </div>

        {/* Export/Import Section */}
        <div className="card mt-8">
          <h2 className="text-2xl font-black mb-4">📁 {t('exportImport.title')}</h2>
          <p className="text-gray-600 mb-4">
            {t('exportImport.description')}
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => setShowExportModal(true)}
              className="px-4 py-2 border-2 border-black rounded-lg font-bold bg-primary shadow-hard-sm hover:opacity-90 transition-opacity"
            >
              📤 {t('exportImport.export')}
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="px-4 py-2 border-2 border-black rounded-lg font-bold bg-white shadow-hard-sm hover:bg-gray-100 transition-colors"
            >
              📥 {t('exportImport.import')}
            </button>
          </div>
        </div>

        {/* Export Modal */}
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
        />

        {/* Import Modal */}
        <ImportModal
          isOpen={showImportModal}
          onClose={() => setShowImportModal(false)}
        />

        {/* Notifications Section */}
        <div className="card mt-8">
          <h2 className="text-2xl font-black mb-4">🔔 {t('notifications.title')}</h2>
          
          {!pushSupported ? (
            <div className="p-4 bg-gray-100 border-2 border-black rounded-lg">
              <p className="text-gray-600">{t('notifications.notSupported')}</p>
            </div>
          ) : pushPermission === 'denied' ? (
            <div className="p-4 bg-red-100 border-2 border-black rounded-lg">
              <p className="font-bold text-red-700">{t('notifications.blocked')}</p>
              <p className="text-sm text-gray-600 mt-1">{t('notifications.blockedHint')}</p>
            </div>
          ) : pushSubscribed ? (
            <div>
              <div className="flex items-center gap-3 p-4 bg-green-100 border-2 border-black rounded-lg mb-4">
                <span className="text-3xl">✅</span>
                <div>
                  <p className="font-bold text-green-700">{t('notifications.active')}</p>
                  <p className="text-sm text-gray-600">{t('notifications.activeDesc')}</p>
                </div>
              </div>
              <button
                onClick={handleDisablePush}
                disabled={pushLoading}
                className="px-4 py-2 border-2 border-black rounded-lg font-bold bg-white hover:bg-gray-100 disabled:opacity-50"
              >
                {pushLoading ? t('common:buttons.loading') : t('notifications.disable')}
              </button>
            </div>
          ) : (
            <div>
              <p className="text-gray-700 mb-4">{t('notifications.description')}</p>
              <button
                onClick={handleEnablePush}
                disabled={pushLoading}
                className="px-4 py-2 border-2 border-black rounded-lg font-bold bg-primary shadow-hard-sm hover:opacity-90 disabled:opacity-50"
              >
                {pushLoading ? t('common:buttons.loading') : t('notifications.enable')}
              </button>
            </div>
          )}
        </div>

        {/* PWA Install Section */}
        <div className="card mt-8">
          <h2 className="text-2xl font-black mb-4">📱 {t('pwa.title')}</h2>
          
          {isInstalled ? (
            <div className="flex items-center gap-3 p-4 bg-green-100 border-2 border-black rounded-lg">
              <span className="text-3xl">✅</span>
              <div>
                <p className="font-bold text-green-700">{t('pwa.installed')}</p>
                <p className="text-sm text-gray-600">
                  {t('pwa.installedDesc')}
                </p>
              </div>
            </div>
          ) : isInstallable ? (
            <div>
              <p className="text-gray-700 mb-4">
                {t('pwa.installPrompt')}
              </p>
              <button
                onClick={handleInstallClick}
                className="btn-primary inline-flex items-center gap-2"
              >
                <span className="text-xl">📲</span>
                {t('pwa.installButton')}
              </button>
            </div>
          ) : (
            <div className="p-4 bg-blue-50 border-2 border-black rounded-lg">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">📱</span>
                <p className="font-bold text-gray-700">{t('pwa.title')}</p>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                {t('pwa.installInstructions')}
              </p>
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="font-bold">Chrome/Edge:</span>
                  <span className="text-gray-600">{t('pwa.chromeInstructions')}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold">Safari (iOS):</span>
                  <span className="text-gray-600">{t('pwa.safariInstructions')}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default SettingsPage;
